/* eslint-disable react/jsx-props-no-spreading */
import { FunctionComponent, useCallback, useState } from 'react';
import { TableProps } from './types';
import { usePagination } from './usePagination';

const DEFAULT_PAGE_SIZE = 10;

type PaginatedTableProps = TableProps;

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowData: any[];
  pageSize?: number;
  totalItems?: number;
};

export const usePaginatedTable = ({ rowData, pageSize = DEFAULT_PAGE_SIZE, totalItems }: Props) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(
    pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
  );

  const { paginatedData, lastPageNumber } = usePagination({
    pageNumber: currentPageNumber,
    pageSize: currentPageSize,
    rowData,
    totalItems,
  });

  const next = useCallback(() => {
    setCurrentPageNumber(pageNumber =>
      pageNumber === lastPageNumber ? lastPageNumber : pageNumber + 1,
    );
  }, [lastPageNumber]);

  const previous = useCallback(() => {
    setCurrentPageNumber(pageNumber => (pageNumber === 1 ? 1 : pageNumber - 1));
  }, []);

  const goToFirst = useCallback(() => {
    setCurrentPageNumber(1);
  }, []);

  const goToLast = useCallback(() => {
    setCurrentPageNumber(lastPageNumber);
  }, [lastPageNumber]);

  const goToPage = useCallback(
    (pageNumber: number) => {
      const pageNumberToSet =
        pageNumber < 1 || pageNumber > lastPageNumber ? currentPageNumber : pageNumber;
      setCurrentPageNumber(pageNumberToSet);
    },
    [currentPageNumber, lastPageNumber],
  );

  const setPageSize = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize);
      goToFirst();
    },
    [goToFirst],
  );

  const Table = useCallback<FunctionComponent<PaginatedTableProps>>(
    ({ ...props }) => {
      return <Table {...props} rowData={paginatedData || []} />;
    },
    [paginatedData],
  );

  return {
    Table,
    currentPageNumber,
    lastPageNumber,
    currentPageSize,
    paginatedData,
    next,
    previous,
    goToFirst,
    goToLast,
    goToPage,
    setPageSize,
  };
};
